import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { UwEligibility } from 'types/uwService';
import { getUwByEnrollmentPeriodId, INITIAL_UW_RESULT_OBJECT } from 'api/operations/uwService';
import { EnrollmentStage, EnrollmentType, FinalBrowsingCriteria } from 'types/enrollmentService';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { checkIfCuttingOff } from 'global/utils/checkIfCuttingOff';
import { INITIAL_COMPLETE_ELECTION_CHECK } from 'store/domain/electionStore';
import { CompleteElectionsCheck } from 'types/electionService';
import { completeEnrollment } from 'api/operations/enrollmentService';
import { getConfigByDate } from 'api/operations/domainServiceV2';
import { Config } from 'types/domainServiceV2';
import { isEnrollmentPeriodUnavailableToResubmit } from 'global/checklists-helpers/ChecklistHelper';
import { overrideRates } from 'api/operations/productServiceV2';

export class ReviewSubmitStore {
  rootStore: RootStore;
  uwResults: UwEligibility;
  loading = false;
  public enrollmentModule: EnrollmentPeriodModule = new EnrollmentPeriodModule();
  public domainConfig: Config | null = null;
  daysLeft = 0;
  electionsCheck: CompleteElectionsCheck = INITIAL_COMPLETE_ELECTION_CHECK;
  isWarningModalOpen = false;

  private setLoading(status: boolean): void {
    this.loading = status;
  }

  public setIsWarningModalOpen(status: boolean): void {
    this.isWarningModalOpen = status;
  }

  public get isSubmitNotEligible(): boolean {
    const cutOffSubmitIneligible = this.daysLeft === 0;

    if (!this.uwResults) {
      return true;
    }
    return cutOffSubmitIneligible || this.isUwRulesNotPass;
  }

  public get isAlreadySubmitted(): boolean {
    return this.enrollmentModule.enrollmentCoreData?.stage === EnrollmentStage.SUBMITTED;
  }

  public get isElectionValid(): boolean {
    const domain = this.rootStore.domainStoreV2.domainHeader;
    const {
      isProductLinesQuestionAnswered,
      isWaiverQuestionAnswered,
      subsWithClass,
      allWithClasses,
      isDependentOnProductLineValid,
      generalQuestionAnswered,
    } = toJS(this.electionsCheck);

    const areClassesValid = subsWithClass === 0 || (domain.properties.eeClassesRequired ? allWithClasses : true);
    return (
      isProductLinesQuestionAnswered &&
      isWaiverQuestionAnswered &&
      areClassesValid &&
      isDependentOnProductLineValid &&
      generalQuestionAnswered
    );
  }

  public get enrollmentPeriodCannotBeResubmitted(): boolean {
    if (!this.enrollmentModule.enrollmentCoreData || !this.domainConfig) {
      return false;
    }
    return isEnrollmentPeriodUnavailableToResubmit(
      this.enrollmentModule.enrollmentCoreData.rateSegment,
      this.enrollmentModule.enrollmentCoreData.stage,
      this.enrollmentModule.enrollmentCoreData.type,
      this.enrollmentModule.postSubmitChecklist !== null,
      this.domainConfig,
    );
  }

  public get isFinalRatesMissing(): boolean {
    return this.uwResults.result.some((result) => result.products.some((product) => product.final.rates.length === 0));
  }

  public get isUwRulesNotPass(): boolean {
    return this.uwResults.result.some((result) => result.eligibilityStatus === 'NOT_ELIGIBLE');
  }

  public async getUwResults(groupId: string, periodId: string): Promise<void> {
    this.setLoading(true);
    const promisesResult = await Promise.all([
      this.enrollmentModule.uploadEnrollmentPeriod(periodId),
      getUwByEnrollmentPeriodId(groupId, periodId),
      this.rootStore.electionStore.checkElection(groupId, periodId),
    ]);

    await runInAction(async () => {
      const domainConfig = await getConfigByDate(
        this.rootStore.domainStoreV2.sponsorId,
        this.enrollmentModule.enrollmentCoreData!.effectiveDate,
      );
      runInAction(() => {
        this.domainConfig = domainConfig;
      });
    });
    runInAction(() => {
      this.uwResults = promisesResult[1];
      this.electionsCheck = promisesResult[2].data.getSubsValidations;
      if (promisesResult[2].data.getSubsValidations.subsWithoutElection > 0) {
        this.setIsWarningModalOpen(true);
      }
      this.daysLeft = checkIfCuttingOff(
        this.enrollmentModule.enrollmentCoreData!.effectiveDate,
        this.retroActiveDaysAmount(),
      ).cutOffInDays;
      this.setLoading(false);
    });
  }

  public async submitEnrollmentPeriod(): Promise<void> {
    this.setLoading(true);
    const cartId = this.enrollmentModule.enrollmentProposalData?.confirmedProposal?.cartId;
    if (!cartId) {
      throw new Error('Cart ID not found');
    }
    const overrideData = this.uwResults.result.flatMap((result) =>
      result.products.map((product) => ({
        portfolioId:
          this.enrollmentModule.enrollmentProposalData?.confirmedProposal?.productLines.find(
            (line) => line.id === result.productLineId,
          )?.confirmedPortfolioId || '',
        productId: product.id,
        bcIndex: -2,
        rates: product.final.rates,
      })),
    );

    await overrideRates(cartId, overrideData);
    await completeEnrollment(
      this.enrollmentModule.enrollmentCoreData!.id,
      this.rootStore.userStore.userFullName,
      this.buildFinalBrowsingCriteria(),
    );
    runInAction(() => {
      this.setLoading(false);
    });
  }

  public get isNewSaleEnrollmentPeriod(): boolean {
    return this.enrollmentModule.enrollmentCoreData?.type === EnrollmentType.NEW_SALE;
  }

  private buildFinalBrowsingCriteria(): FinalBrowsingCriteria {
    const object = this.uwResults.result.reduce(
      (acc, current) => ({
        ...acc,
        [current.productLineId]: current.products[0].final.browsingCriteria,
      }),
      {},
    );
    return object;
  }

  private retroActiveDaysAmount(): number {
    return this.isNewSaleEnrollmentPeriod
      ? this.rootStore.domainStoreV2.domainHeader.properties.retroActiveDaysAmount || 10
      : 0;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.uwResults = INITIAL_UW_RESULT_OBJECT;

    makeObservable(this, {
      loading: observable,
      daysLeft: observable,
      uwResults: observable,
      domainConfig: observable,
      isWarningModalOpen: observable,
      electionsCheck: observable,
      isSubmitNotEligible: computed,
      isAlreadySubmitted: computed,
      isElectionValid: computed,
      isUwRulesNotPass: computed,
      enrollmentPeriodCannotBeResubmitted: computed,
      submitEnrollmentPeriod: action.bound,
      getUwResults: action.bound,
      setIsWarningModalOpen: action.bound,
    });
  }
}
